import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Inbox } from 'lucide-react';
import { useUi, type View } from '@/lib/store';
import { composeFromDraft } from '@/lib/compose';
import { useMessage } from '@/lib/queries';
import { MessageList } from '@/components/MessageList';
import { Reader } from '@/components/Reader';
import { Empty, Kbd } from '@/components/ui';

export function Mail({ view }: { view: Extract<View, { kind: 'mail' }> }) {
  const selectedId = useUi((s) => s.selectedId);
  const select = useUi((s) => s.select);
  const selected = useMessage(selectedId);
  const [width, setWidth] = useState(() => Number(localStorage.getItem('dts.listWidth')) || 420);
  const dragging = useRef(false);

  // Drafts open in the composer rather than the reader.
  useEffect(() => {
    if (selected.data?.summary.folder === 'Drafts') {
      composeFromDraft(selected.data);
      select(null);
    }
  }, [selected.data, select]);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!dragging.current) return;
      const sidebar = document.querySelector('aside')?.getBoundingClientRect().right ?? 0;
      const w = Math.min(640, Math.max(320, e.clientX - sidebar));
      setWidth(w);
    };
    const up = () => {
      if (dragging.current) localStorage.setItem('dts.listWidth', String(width));
      dragging.current = false;
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
  }, [width]);

  return (
    <div className="flex h-full min-h-0">
      <div className="relative h-full shrink-0 border-r border-line" style={{ width }}>
        <MessageList view={view} />
        <div
          onMouseDown={() => {
            dragging.current = true;
            document.body.style.cursor = 'col-resize';
          }}
          className="absolute -right-1 top-0 z-10 h-full w-2 cursor-col-resize hover:bg-accent/20"
        />
      </div>
      <div className="min-w-0 flex-1">
        <AnimatePresence mode="wait">
          {selectedId ? (
            <motion.div key={selectedId} className="h-full" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.16 }}>
              <Reader id={selectedId} />
            </motion.div>
          ) : (
            <motion.div key="empty" className="h-full" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Empty icon={<Inbox />} title="Select a conversation" hint="AI summarizes it and suggests replies the moment you open it.">
                <div className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-[12px] text-faint">
                  <span><Kbd>J</Kbd> <Kbd>K</Kbd> navigate</span>
                  <span><Kbd>R</Kbd> reply</span>
                  <span><Kbd>E</Kbd> archive</span>
                  <span><Kbd>C</Kbd> compose</span>
                  <span><Kbd>Ctrl</Kbd> <Kbd>K</Kbd> commands</span>
                  <span><Kbd>Ctrl</Kbd> <Kbd>J</Kbd> ask AI</span>
                </div>
              </Empty>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
